import React, { useMemo, useState } from 'react';
import { Eye, EyeOff, LogIn, UserPlus, Mail, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { GoogleAuthProvider, linkWithCredential, fetchSignInMethodsForEmail, AuthCredential } from 'firebase/auth';
import { auth } from '../config/firebase';
import { BRAND_MARK_SRC, PRODUCT_NAME } from '../brand';
import AuthFlowShell from './AuthFlowShell';
import './LoginPage.css';

interface LoginPageProps {
  embedded?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ embedded = false }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showGoogleTermsPrompt, setShowGoogleTermsPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [linkEmail, setLinkEmail] = useState<string | null>(null);
  const [linkCredential, setLinkCredential] = useState<AuthCredential | null>(null);
  const [linkPassword, setLinkPassword] = useState('');

  const { login, signup, signInWithGoogle, requestPasswordReset } = useAuth();

  const isLinkingFlow = useMemo(() => Boolean(linkEmail && linkCredential), [linkEmail, linkCredential]);
  const errorId = error ? 'auth-error' : undefined;
  const termsErrorMessage = 'Please agree to the Terms and acknowledge the Privacy Policy to continue';
  const missingFieldsError = 'Please fill in all fields';
  const passwordMismatchError = 'Passwords do not match';
  const passwordLengthError = 'Password must be at least 6 characters';
  const emailFieldInvalid = Boolean(
    (error === missingFieldsError && !email) ||
    error === 'No account found with this email address' ||
    error === 'An account with this email already exists' ||
    error === 'Invalid email address'
  );
  const passwordFieldInvalid = Boolean(
    (error === missingFieldsError && !password) ||
    error === 'Incorrect password' ||
    error === 'Password is too weak' ||
    error === passwordLengthError
  );
  const confirmPasswordFieldInvalid = Boolean(
    !isLogin && (
      (error === missingFieldsError && !confirmPassword) ||
      error === passwordMismatchError ||
      error === passwordLengthError
    )
  );
  const showTermsConsent = !isLogin || showGoogleTermsPrompt;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError(missingFieldsError);
      return;
    }

    if (!isLogin && !acceptedTerms) {
      setError(termsErrorMessage);
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError(passwordMismatchError);
      return;
    }

    if (password.length < 6) {
      setError(passwordLengthError);
      return;
    }

    try {
      setError('');
      setResetMessage('');
      setLoading(true);
      
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password);
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      
      // Handle Firebase auth errors with user-friendly messages
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password');
          break;
        case 'auth/email-already-in-use':
          setError('An account with this email already exists');
          break;
        case 'auth/weak-password':
          setError('Password is too weak');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later');
          break;
        default:
          setError('Authentication failed. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccounts = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkEmail || !linkCredential) {
      return;
    }
    if (!linkPassword) {
      setError('Please enter your password to link accounts');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const userCredential = await login(linkEmail, linkPassword);
      await linkWithCredential(userCredential.user, linkCredential);

      setLinkEmail(null);
      setLinkCredential(null);
      setLinkPassword('');
    } catch (error: any) {
      console.error('Account linking error:', error);
      switch (error.code) {
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please try again later.');
          break;
        default:
          setError('Failed to link accounts. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setShowGoogleTermsPrompt(true);
      setError(termsErrorMessage);
      return;
    }

    try {
      setError('');
      setResetMessage('');
      setLoading(true);
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Google auth error:', error);
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          break;
        case 'auth/popup-blocked':
          setError('Popup was blocked. Please allow popups for this site');
          break;
        case 'auth/account-exists-with-different-credential':
          try {
            const pendingCredential = GoogleAuthProvider.credentialFromError(error);
            const existingEmail = error?.customData?.email as string | undefined;
            if (!pendingCredential || !existingEmail) {
              setError('An account already exists with this email using a different sign-in method');
              break;
            }

            const methods = await fetchSignInMethodsForEmail(auth, existingEmail);
            if (!methods.includes('password')) {
              setError('An account already exists with this email. Please sign in using your original sign-in method.');
              break;
            }

            setLinkEmail(existingEmail);
            setLinkCredential(pendingCredential);
            setLinkPassword('');
            setIsLogin(true);
            setEmail(existingEmail);
            setPassword('');
            setConfirmPassword('');
            setError('Enter your password to link Google sign-in to your existing account');
          } catch (innerError) {
            console.error('Failed to initialize account linking flow:', innerError);
            setError('An account already exists with this email using a different sign-in method');
          }
          break;
        default:
          setError('Google sign-in failed. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError('Enter your email address to reset your password');
      setResetMessage('');
      return;
    }

    try {
      setError('');
      setResetMessage('');
      setLoading(true);
      await requestPasswordReset(normalizedEmail);
      setResetMessage('If an account exists for this email, a reset link has been sent.');
    } catch (error: any) {
      console.error('Password reset error:', error);
      switch (error.code) {
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/user-not-found':
          setResetMessage('If an account exists for this email, a reset link has been sent.');
          break;
        case 'auth/too-many-requests':
          setError('Too many reset attempts. Please try again later');
          break;
        default:
          setError('Unable to send reset email right now. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setResetMessage('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setLinkEmail(null);
    setLinkCredential(null);
    setLinkPassword('');
    setAcceptedTerms(false);
    setShowGoogleTermsPrompt(false);
  };

  const handleTermsAcceptedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isAccepted = e.target.checked;
    setAcceptedTerms(isAccepted);
    if (isAccepted) {
      setShowGoogleTermsPrompt(false);
    }
    if (isAccepted && error === termsErrorMessage) {
      setError('');
    }
  };

  const content = (
        <div className="login-container auth-flow-card">
          <div className="login-header">
            <div className="login-brand-lockup" aria-label={PRODUCT_NAME}>
              <img className="login-brand-mark" src={BRAND_MARK_SRC} alt="" aria-hidden="true" />
              <h1>{PRODUCT_NAME}</h1>
            </div>
            <p>
              {isLogin
                ? 'Sign in to your account to access your video indexes'
                : 'Create an account to start organizing your videos'
              }
            </p>
          </div>

          {isLinkingFlow && (
          <form onSubmit={handleLinkAccounts} className="link-form" aria-label="Link accounts form">
            <div className="link-header">
              <h2>Link Accounts</h2>
              <p>
                We found an existing account for <strong>{linkEmail}</strong>. Enter your password to link Google sign-in.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="linkPassword">Password</label>
              <div className="input-with-icon">
                <Lock size={18} className="input-icon" />
                <input
                  id="linkPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="form-input"
                  disabled={loading}
                  required
                  minLength={6}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div id="auth-error" className="auth-error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <div className="link-actions">
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Linking...' : 'Link and Continue'}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setLinkEmail(null);
                  setLinkCredential(null);
                  setLinkPassword('');
                  setError('');
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {!isLinkingFlow && (
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-with-icon">
              <Mail size={18} className="input-icon" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="form-input"
                disabled={loading}
                required
                aria-invalid={emailFieldInvalid}
                aria-describedby={errorId}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-with-icon">
              <Lock size={18} className="input-icon" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="form-input"
                disabled={loading}
                required
                minLength={6}
                aria-invalid={passwordFieldInvalid}
                aria-describedby={errorId}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {isLogin && (
            <div className="login-inline-actions">
              <button
                type="button"
                className="link-button"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>
          )}

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-with-icon">
                <Lock size={18} className="input-icon" />
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="form-input"
                  disabled={loading}
                  required={!isLogin}
                  minLength={6}
                  aria-invalid={confirmPasswordFieldInvalid}
                  aria-describedby={errorId}
                />
              </div>
            </div>
          )}

          {showTermsConsent && (
            <div className="terms-consent">
              <input
                id="termsAccepted"
                type="checkbox"
                checked={acceptedTerms}
                onChange={handleTermsAcceptedChange}
                disabled={loading}
                aria-invalid={error === termsErrorMessage ? true : undefined}
                aria-describedby={errorId}
              />
              <label htmlFor="termsAccepted">
                I agree to the <a href="/terms">Terms</a> and acknowledge the{' '}
                <a href="/privacy">Privacy Policy</a>.
              </label>
            </div>
          )}

          {error && (
            <div id="auth-error" className="auth-error-message" role="alert" aria-live="polite">
              {error}
            </div>
          )}
          {resetMessage && (
            <div className="success-message">
              {resetMessage}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? (
              'Please wait...'
            ) : (
              <>
                {isLogin ? <LogIn size={18} /> : <UserPlus size={18} />}
                {isLogin ? 'Sign In' : 'Create Account'}
              </>
            )}
          </button>
        </form>
        )}

        {!isLinkingFlow && (
        <div className="divider">
          <span>or</span>
        </div>
        )}

        {!isLinkingFlow && (
        <button
          type="button"
          className="google-button"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        )}

        {!isLinkingFlow && (
          <>
            <div className="login-footer">
              <p>
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  className="link-button"
                  onClick={toggleMode}
                  disabled={loading}
                >
                  {isLogin ? 'Create one here' : 'Sign in here'}
                </button>
              </p>
            </div>

            <nav className="legal-footer" aria-label="Legal">
              <a href="/terms">Terms</a>
              <a href="/privacy">Privacy</a>
              <a href="/cookies">Cookies</a>
              <a href="/accessibility">Accessibility</a>
            </nav>
          </>
        )}
        </div>
  );

  if (embedded) {
    return content;
  }

  return <AuthFlowShell>{content}</AuthFlowShell>;
};

export default LoginPage;
